import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useApp } from '@/context/AppContext';
import { getLocalSales } from '@/lib/offlineDb';
import { money } from '@/lib/helpers';
import { RefreshCw, CheckCircle2, AlertTriangle, WifiOff, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch (e) { return ''; }
};

// A quick, reassuring view of offline sales that haven't reached the server yet,
// with a manual "Sync now" so staff never wonder whether a bill was saved.
export function QueuedSalesDialog({ open, onOpenChange }) {
  const { online, syncing, flushPending, refreshPending } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = await getLocalSales();
      const pending = all.filter((s) => !s.synced);
      pending.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      setRows(pending);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);
  // Refresh the list whenever a background sync finishes.
  useEffect(() => { if (open && !syncing) load(); /* eslint-disable-next-line */ }, [syncing]);

  const syncNow = async () => {
    if (!online) { toast.error('You are offline. Connect to sync.'); return; }
    await flushPending();
    await refreshPending();
    await load();
    const remaining = (await getLocalSales()).filter((s) => !s.synced).length;
    if (remaining === 0) toast.success('All sales synced');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="queued-sales-dialog">
        <DialogHeader><DialogTitle>Queued sales</DialogTitle></DialogHeader>

        {!online && (
          <div className="flex items-center gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs font-medium text-amber-700 dark:text-amber-300" data-testid="queued-offline-note">
            <WifiOff className="h-3.5 w-3.5" /> You&apos;re offline — sales will sync automatically when you reconnect.
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center" data-testid="queued-empty">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            <p className="text-sm font-medium">All caught up</p>
            <p className="text-xs text-muted-foreground">No sales are waiting to sync.</p>
          </div>
        ) : (
          <div className="divide-y divide-border" data-testid="queued-list">
            {rows.map((s) => (
              <div key={s.client_txn_id} className="flex items-center gap-3 py-2.5" data-testid={`queued-sale-${s.client_txn_id}`}>
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${s.conflict ? 'bg-destructive/10 text-destructive' : 'bg-secondary text-primary'}`}>
                  {s.conflict ? <AlertTriangle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{s.invoice_no}{s.customer?.name ? ` · ${s.customer.name}` : ''}</p>
                  <p className="truncate text-[11px] text-muted-foreground">{fmtTime(s.created_at)} · {(s.items?.length || 0)} item{(s.items?.length || 0) === 1 ? '' : 's'}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm font-semibold">{money(s.net)}</p>
                  <span className={`text-[10px] font-semibold uppercase ${s.conflict ? 'text-destructive' : 'text-muted-foreground'}`}>{s.conflict ? 'Conflict' : 'Queued'}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className="rounded-xl" onClick={() => onOpenChange(false)} data-testid="queued-close">Close</Button>
          <Button className="rounded-xl" onClick={syncNow} disabled={!online || syncing || rows.length === 0} data-testid="queued-sync-now">
            {syncing ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Syncing…</> : <><RefreshCw className="mr-1.5 h-4 w-4" />Sync now</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
